import {
  Fragment,
  useCallback,
  useLayoutEffect,
  useRef,
  type ComponentProps,
} from 'react';
import {Textarea} from '@ui/shadcn/textarea';

/** Textarea с цветными диапазонами: ввод, выделение и история undo остаются нативными. */
export const HighlightedTextarea = ({
  value,
  ranges,
  ref,
  style,
  ...props
}: Omit<ComponentProps<typeof Textarea>, 'value'> & {
  value: string;
  ranges: {start: number; end: number}[];
}) => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const setRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      inputRef.current = node;
      if (typeof ref === 'function') return ref(node);
      if (ref) ref.current = node;
    },
    [ref],
  );
  const sync = useCallback(() => {
    const input = inputRef.current;
    const mirror = mirrorRef.current;
    if (!input || !mirror) return;
    const computed = window.getComputedStyle(input);
    for (const property of [
      'fontFamily',
      'fontSize',
      'fontWeight',
      'fontStyle',
      'fontVariant',
      'lineHeight',
      'letterSpacing',
      'textAlign',
      'textIndent',
      'textTransform',
      'tabSize',
      'paddingTop',
      'paddingRight',
      'paddingBottom',
      'paddingLeft',
      'wordBreak',
      'overflowWrap',
      'direction',
    ] as const) {
      mirror.style[property] = computed[property];
    }
    Object.assign(mirror.style, {
      left: `${input.clientLeft}px`,
      top: `${input.clientTop}px`,
      width: `${input.clientWidth}px`,
      height: `${input.clientHeight}px`,
    });
    mirror.scrollTop = input.scrollTop;
    mirror.scrollLeft = input.scrollLeft;
  }, []);
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(input);
    input.addEventListener('scroll', sync, {passive: true});
    window.addEventListener('resize', sync);
    return () => {
      observer.disconnect();
      input.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
  }, [sync]);
  useLayoutEffect(sync, [sync, value, ranges, props.className]);
  const parts = [];
  let offset = 0;
  for (const {start, end} of ranges) {
    if (start < offset || end <= start || end > value.length) continue;
    if (start > offset)
      parts.push(
        <Fragment key={`text-${offset}`}>
          {value.slice(offset, start)}
        </Fragment>,
      );
    parts.push(
      <span key={`range-${start}`} className='text-[var(--color-primary)]'>
        {value.slice(start, end)}
      </span>,
    );
    offset = end;
  }
  parts.push(
    <Fragment key='tail'>
      {value.slice(offset)}
      {'\n'}
    </Fragment>,
  );
  return (
    <div className='relative w-full'>
      <div
        ref={mirrorRef}
        aria-hidden='true'
        className='pointer-events-none absolute select-none overflow-hidden whitespace-pre-wrap break-words text-[var(--color-foreground)]'
        style={{boxSizing: 'border-box', opacity: props.disabled ? 0.5 : 1}}
      >
        {parts}
      </div>
      <Textarea
        {...props}
        value={value}
        ref={setRef}
        style={{
          ...style,
          color: 'transparent',
          caretColor: 'var(--color-foreground)',
        }}
      />
    </div>
  );
};
